import {
  GoogleSignInReturnPath,
  TwoFactorMethodType,
} from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";

/**
 * Pages that may start a Google sign-in. Re-exported from the wire contract
 * rather than re-declared: the backend whitelists the same values when it signs
 * the OAuth `state`, so the two must never drift.
 */
export type { GoogleSignInReturnPath };

export enum GoogleCallbackStatus {
  Success = "success",
  TwoFactor = "two_factor",
  /**
   * The callback proved who the person is and stopped there, because the
   * account it belongs to does not exist yet. Only the embedded show sign-up
   * sees this.
   */
  Identity = "identity",
  Error = "error",
  Denied = "denied",
}

/**
 * Every outcome the callback can report, as it arrives in the redirect
 * fragment. Mirrors `GoogleSignInReason` on the API — if the backend gains a
 * reason, add it here too.
 */
export enum GoogleSignInReason {
  AccessDenied = "access_denied",
  ProviderError = "provider_error",
  MissingCode = "missing_code",
  InvalidState = "invalid_state",
  EmailNotVerified = "email_not_verified",
  UserNotFound = "user_not_found",
  AccountAlreadyLinked = "google_account_mismatch",
  OauthExchangeFailed = "oauth_exchange_failed",
  /** Reported only on the two sign-up pages, which render their own copy. */
  InviteInvalid = "invite_invalid",
  InviteEmailMismatch = "invite_email_mismatch",
  /** Reported only with an identity, on the embedded show sign-up. */
  EmailInUseByAnotherRole = "email_in_use_by_another_role",
}

export type GoogleCallbackResult =
  | {
      status: GoogleCallbackStatus.Success;
      customToken: string;
      linked: boolean;
    }
  | {
      status: GoogleCallbackStatus.Identity;
      /** Spent by `embedded/create` and then by `auth/google/identity-login`. */
      identityToken: string;
      /** The verified address, for the read-only Email field. */
      email: string;
      /**
       * The address belongs to an Organiser, Supplier or Admin account, so it
       * cannot sign up as an exhibitor — known before the form is filled in.
       */
      isEmailInUseByAnotherRole: boolean;
    }
  | {
      status: GoogleCallbackStatus.TwoFactor;
      pendingToken: string;
      method: TwoFactorMethodType;
      methods: TwoFactorMethodType[];
      linked: boolean;
    }
  | {
      status: GoogleCallbackStatus.Error | GoogleCallbackStatus.Denied;
      reason: GoogleSignInReason | null;
      /** Set only for `InviteEmailMismatch` — the address the invite was sent to. */
      invitedEmail: string | null;
    };

const isGoogleSignInReason = (
  value: string | null
): value is GoogleSignInReason =>
  !!value && (Object.values(GoogleSignInReason) as string[]).includes(value);

const GOOGLE_LINKED_TOAST_KEY = "ffair_google_account_linked";

/**
 * Sends the browser to Google. The current query string goes with it inside the
 * signed state, so `redirectPathAfterLogin`, `showId` and an invite `token`
 * survive the round trip and are back in the URL when the callback returns.
 */
export const startGoogleSignIn = async (
  returnPath: GoogleSignInReturnPath,
  /**
   * Extra query parameters to carry through the round trip on top of the
   * current ones. The embedded sign-up puts its `uid` here: its route holds
   * that value in the PATH, and the whitelist the backend compares against is
   * a list of literals, so the concrete segment has to travel in the query.
   */
  extraQuery?: Record<string, string>
): Promise<void> => {
  const query = new URLSearchParams(window.location.search);

  Object.entries(extraQuery ?? {}).forEach(([key, value]) =>
    query.set(key, value)
  );

  const search = query.toString();

  const { redirectUrl } = await apiV2.auth.getAdminGoogleRedirectUrl({
    returnPath,
    returnQuery: search ? `?${search}` : "",
  });

  window.location.href = redirectUrl;
};

const parseMethods = (raw: string | null): TwoFactorMethodType[] =>
  (raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean) as TwoFactorMethodType[];

/**
 * Reads the outcome the callback left in the URL fragment WITHOUT touching the
 * URL. Returns `null` when this is an ordinary page load.
 *
 * Kept separate from stripping because a caller may need to read during render
 * — `React.StrictMode` invokes render twice in development, and a read that
 * consumed the fragment would hand the second invocation nothing.
 */
export const parseGoogleCallbackResult = (): GoogleCallbackResult | null => {
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (!rawHash) return null;

  const params = new URLSearchParams(rawHash);

  if (params.get("provider") !== "google") return null;

  const status = params.get("status") as GoogleCallbackStatus | null;
  const linked = params.get("linked") === "1";
  const rawReason = params.get("reason");

  const asFailure = (
    reason: GoogleSignInReason | null
  ): GoogleCallbackResult => ({
    status:
      status === GoogleCallbackStatus.Denied
        ? GoogleCallbackStatus.Denied
        : GoogleCallbackStatus.Error,
    reason,
    invitedEmail: params.get("invitedEmail"),
  });

  if (status === GoogleCallbackStatus.Success) {
    const customToken = params.get("customToken");

    if (!customToken) return asFailure(null);

    return { status: GoogleCallbackStatus.Success, customToken, linked };
  }

  if (status === GoogleCallbackStatus.Identity) {
    const identityToken = params.get("identityToken");
    const email = params.get("email");

    if (!identityToken || !email) return asFailure(null);

    return {
      status: GoogleCallbackStatus.Identity,
      identityToken,
      email,
      isEmailInUseByAnotherRole:
        rawReason === GoogleSignInReason.EmailInUseByAnotherRole,
    };
  }

  if (status === GoogleCallbackStatus.TwoFactor) {
    const pendingToken = params.get("pendingToken");
    const method = params.get("method") as TwoFactorMethodType | null;
    const methods = parseMethods(params.get("methods"));

    if (!pendingToken || !method || !methods.length) return asFailure(null);

    return {
      status: GoogleCallbackStatus.TwoFactor,
      pendingToken,
      method,
      methods,
      linked,
    };
  }

  // An unrecognised reason degrades to the generic message rather than being
  // echoed back as a raw code.
  return asFailure(isGoogleSignInReason(rawReason) ? rawReason : null);
};

/**
 * Drops the fragment, so a reload or a copied URL cannot replay a one-time
 * token. Safe to call when there is nothing to drop.
 */
export const stripGoogleCallbackFragment = (): void => {
  if (!window.location.hash) return;

  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${window.location.search}`
  );
};

/**
 * Reads the outcome and consumes it in one step, for callers that do this from
 * an effect and never during render.
 */
export const readGoogleCallbackResult = (): GoogleCallbackResult | null => {
  const result = parseGoogleCallbackResult();

  if (result) stripGoogleCallbackFragment();

  return result;
};

/**
 * The "your Google account is now linked" toast belongs on the portal page the
 * person lands on, not on the login page, so the flag is parked in
 * sessionStorage across the navigation.
 */
export const markGoogleAccountLinked = (): void => {
  try {
    sessionStorage.setItem(GOOGLE_LINKED_TOAST_KEY, "1");
  } catch {
    // Losing the flag only costs the toast.
  }
};

export const consumeGoogleAccountLinked = (): boolean => {
  try {
    if (sessionStorage.getItem(GOOGLE_LINKED_TOAST_KEY) !== "1") return false;

    sessionStorage.removeItem(GOOGLE_LINKED_TOAST_KEY);

    return true;
  } catch {
    return false;
  }
};

/**
 * Tolgee key for each failure the callback can report. Anything unrecognised
 * falls back to the generic message rather than echoing a raw reason code.
 */
// `Partial` on purpose: `InviteInvalid` and `InviteEmailMismatch` are absent
// because the two sign-up pages render their own copy for them — the first
// reuses each page's existing expiry wording, the second needs the invited
// address interpolated. A full `Record` would force meaningless login-page
// strings for both.
const GOOGLE_ERROR_KEY_BY_REASON: Partial<
  Record<GoogleSignInReason, string>
> = {
  [GoogleSignInReason.AccessDenied]: "global.login.google_error.cancelled",
  [GoogleSignInReason.EmailNotVerified]:
    "global.login.google_error.email_not_verified",
  [GoogleSignInReason.InvalidState]: "global.login.google_error.invalid_state",
  [GoogleSignInReason.MissingCode]: "global.login.google_error.missing_code",
  [GoogleSignInReason.OauthExchangeFailed]:
    "global.login.google_error.exchange_failed",
  [GoogleSignInReason.ProviderError]:
    "global.login.google_error.provider_error",
  [GoogleSignInReason.UserNotFound]: "global.login.google_error.no_account",
  [GoogleSignInReason.AccountAlreadyLinked]:
    "global.login.google_error.already_linked",
};

export const resolveGoogleErrorKey = (
  reason: GoogleSignInReason | null
): string =>
  (reason && GOOGLE_ERROR_KEY_BY_REASON[reason]) ||
  "global.login.google_error.generic";
